/**
 * Conversation Detailed Report — filter drawer.
 *
 * Same shell and flow as the Agent Performance drawer: opens on arrival, holds
 * the report closed until Start Date and End Date are set, and hands the page
 * a validated filter set.
 *
 * Two chains cascade, and each link only appears once its parent is chosen:
 *
 *   Location → Branch → Department
 *   Campaign → Team   → Agents
 *
 * Revealing them progressively rather than showing five disabled dropdowns
 * keeps the drawer short, and makes it obvious that Branch means "branch of
 * the selected location" rather than "any branch in the tenant". Choosing a
 * parent narrows the child's options and clears any child selection that no
 * longer belongs to the new scope.
 *
 * The remaining catalog filters are rendered from a table rather than written
 * out nine times — they are structurally identical (multi-select of
 * {label,value}), and listing them as data keeps them in a fixed, reviewable
 * order instead of ~130 lines of near-duplicate JSX.
 */
import { useEffect, useMemo, useState, type JSX } from 'react'
import { Dropdown } from 'primereact/dropdown'
import { MultiSelect } from 'primereact/multiselect'
import { Calendar } from 'primereact/calendar'
import { SlidersHorizontal, RotateCcw, UserCog } from 'lucide-react'

import DualListSelect from '../../common/DualListSelect.js'
import ResourceDrawer from '../../admin/ResourceDrawer.js'
import { useOrgTimezone } from '../../../hooks/useOrgTimezone.js'
import {
  useConversationDetailFilterOptions,
  type DetailFilters,
  type DetailFilterOptions,
} from '../../../hooks/useConversationDetailReport.js'

/** The flat catalogs — no parent, always visible. */
const CATALOG_FIELDS: {
  key: keyof DetailFilters
  label: string
  source: keyof DetailFilterOptions
}[] = [
  { key: 'statusIds', label: 'Status', source: 'statuses' },
  { key: 'stateIds', label: 'State', source: 'states' },
  { key: 'typeIds', label: 'Type', source: 'types' },
  { key: 'categoryIds', label: 'Category', source: 'categories' },
  { key: 'subCategoryIds', label: 'Sub Category', source: 'subCategories' },
  { key: 'priorityIds', label: 'Priority', source: 'priorities' },
  { key: 'complexityIds', label: 'Complexity', source: 'complexities' },
  { key: 'urgencyIds', label: 'Urgency', source: 'urgencies' },
  { key: 'impactIds', label: 'Impact', source: 'impacts' },
]

interface Props {
  open: boolean
  onClose: () => void
  onRun: (filters: DetailFilters) => void
  onReset: () => void
  running: boolean
}

/** `YYYY-MM-DD` in LOCAL time — `toISOString()` would shift the day westward. */
function toDateString(d: Date | null): string {
  if (!d) return ''
  const pad = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const EMPTY_IDS: Record<string, string[]> = {}

export default function ConversationDetailFilters({
  open, onClose, onRun, onReset, running,
}: Props): JSX.Element {
  const browserZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
    [],
  )
  const { timezone: orgTimezone } = useOrgTimezone()

  // Start on the resolved timezone if it's already cached (no flash), or the
  // browser zone as a placeholder while the first fetch is in flight.
  const [timezone, setTimezone] = useState(orgTimezone || browserZone)

  // Once the resolved timezone arrives, adopt it — but only if the user hasn't
  // manually changed the dropdown yet (timezone still matches the initial value).
  useEffect(() => {
    if (orgTimezone) setTimezone(orgTimezone)
  }, [orgTimezone])
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [ids, setIds] = useState<Record<string, string[]>>(EMPTY_IDS)
  const [locationIds, setLocationIds] = useState<string[]>([])
  const [branchIds, setBranchIds] = useState<string[]>([])
  const [departmentIds, setDepartmentIds] = useState<string[]>([])
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [teamIds, setTeamIds] = useState<string[]>([])
  const [agentIds, setAgentIds] = useState<string[]>([])
  const [touched, setTouched] = useState(false)

  const { options, loading } = useConversationDetailFilterOptions()

  const timezoneOptions = useMemo(() => {
    const list = options.timezones.map((t) => ({
      label: t.offset ? `${t.label} (${t.offset})` : t.label,
      value: t.value,
    }))
    if (!list.some((t) => t.value === browserZone)) {
      list.unshift({ label: browserZone, value: browserZone })
    }
    return list
  }, [options.timezones, browserZone])

  // Each dependent list is derived from its parent selection, so it can never
  // offer an option that would return nothing.
  const branchOptions = useMemo(
    () => options.branches.filter((b) => b.locationId && locationIds.includes(b.locationId)),
    [options.branches, locationIds],
  )
  const departmentOptions = useMemo(
    () => options.departments.filter((d) => d.branchId && branchIds.includes(d.branchId)),
    [options.departments, branchIds],
  )
  const teamOptions = useMemo(
    () => options.teams.filter((t) => t.campaignIds.some((c) => campaignIds.includes(c))),
    [options.teams, campaignIds],
  )
  const agentOptions = useMemo(
    () => options.agents.filter((a) => a.teamIds.some((t) => teamIds.includes(t))),
    [options.agents, teamIds],
  )

  // Dropping a parent must drop the children it justified — keeping a stale id
  // would silently filter against a scope the user can no longer see. Joined
  // keys, not the arrays: a fresh array identity each render would clear the
  // selection on every unrelated keystroke.
  const branchKey = branchOptions.map((b) => b.value).join(',')
  const departmentKey = departmentOptions.map((d) => d.value).join(',')
  const teamKey = teamOptions.map((t) => t.value).join(',')
  const agentKey = agentOptions.map((a) => a.value).join(',')

  useEffect(() => {
    const allowed = new Set(branchKey ? branchKey.split(',') : [])
    setBranchIds((prev) => (prev.every((v) => allowed.has(v)) ? prev : prev.filter((v) => allowed.has(v))))
  }, [branchKey])
  useEffect(() => {
    const allowed = new Set(departmentKey ? departmentKey.split(',') : [])
    setDepartmentIds((prev) => (prev.every((v) => allowed.has(v)) ? prev : prev.filter((v) => allowed.has(v))))
  }, [departmentKey])
  useEffect(() => {
    const allowed = new Set(teamKey ? teamKey.split(',') : [])
    setTeamIds((prev) => (prev.every((v) => allowed.has(v)) ? prev : prev.filter((v) => allowed.has(v))))
  }, [teamKey])
  useEffect(() => {
    const allowed = new Set(agentKey ? agentKey.split(',') : [])
    setAgentIds((prev) => (prev.every((v) => allowed.has(v)) ? prev : prev.filter((v) => allowed.has(v))))
  }, [agentKey])

  const errors = {
    startDate: !startDate ? 'Start date is required' : '',
    endDate: !endDate
      ? 'End date is required'
      : startDate && endDate < startDate
        ? 'End date must be on or after start date'
        : '',
  }
  const valid = !errors.startDate && !errors.endDate

  const setIdsFor = (key: string, values: string[]): void => {
    setIds((prev) => ({ ...prev, [key]: values }))
  }

  const handleRun = (): void => {
    setTouched(true)
    if (!valid) return
    onRun({
      timezone,
      startDate: toDateString(startDate),
      endDate: toDateString(endDate),
      locationIds,
      branchIds,
      departmentIds,
      campaignIds,
      teamIds,
      statusIds: ids['statusIds'] ?? [],
      stateIds: ids['stateIds'] ?? [],
      typeIds: ids['typeIds'] ?? [],
      categoryIds: ids['categoryIds'] ?? [],
      subCategoryIds: ids['subCategoryIds'] ?? [],
      priorityIds: ids['priorityIds'] ?? [],
      complexityIds: ids['complexityIds'] ?? [],
      urgencyIds: ids['urgencyIds'] ?? [],
      impactIds: ids['impactIds'] ?? [],
      agentIds,
    })
  }

  const handleReset = (): void => {
    setTimezone(orgTimezone)
    setStartDate(null)
    setEndDate(null)
    setIds(EMPTY_IDS)
    setLocationIds([])
    setBranchIds([])
    setDepartmentIds([])
    setCampaignIds([])
    setTeamIds([])
    setAgentIds([])
    setTouched(false)
    onReset()
  }

  const showError = (msg: string): JSX.Element | null =>
    touched && msg ? <small className="mt-1 block text-xs font-medium text-red-600">{msg}</small> : null

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => { handleRun() }}
      saving={running}
      disabled={touched && !valid}
      width="xl"
      saveLabel={running ? 'Running…' : 'Run Report'}
      title="Report Filters"
    >
      <div className="kc3-form">
        <p className="mb-4 flex items-center gap-2 text-xs text-slate-500">
          <SlidersHorizontal className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
          Choose a window, narrow it however you like, then run the report.
        </p>

        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">Reporting window</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Timezone">
            <Dropdown
              value={timezone}
              options={timezoneOptions}
              onChange={(e) => setTimezone(String(e.value))}
              filter
              placeholder="Select timezone"
              className="w-full"
            />
          </Field>

          <Field label="Start Date" required>
            <Calendar
              value={startDate}
              onChange={(e) => setStartDate((e.value as Date | null) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select start date"
              className={`w-full ${touched && errors.startDate ? 'p-invalid' : ''}`}
              maxDate={endDate ?? undefined}
            />
            {showError(errors.startDate)}
          </Field>

          <Field label="End Date" required>
            <Calendar
              value={endDate}
              onChange={(e) => setEndDate((e.value as Date | null) ?? null)}
              dateFormat="yy-mm-dd"
              showIcon
              placeholder="Select end date"
              className={`w-full ${touched && errors.endDate ? 'p-invalid' : ''}`}
              minDate={startDate ?? undefined}
            />
            {showError(errors.endDate)}
          </Field>
        </div>

        {/* Location → Branch → Department. */}
        <Section title="Organisation">
          <Field label="Location">
            <MultiSelect
              value={locationIds}
              options={options.locations}
              onChange={(e) => setLocationIds((e.value as string[]) ?? [])}
              filter
              display="chip"
              maxSelectedLabels={2}
              selectedItemsLabel="{0} locations"
              placeholder={loading ? 'Loading…' : 'All locations'}
              className="w-full"
            />
          </Field>

          {locationIds.length > 0 && (
            <Field label="Branch">
              <MultiSelect
                value={branchIds}
                options={branchOptions}
                onChange={(e) => setBranchIds((e.value as string[]) ?? [])}
                filter
                display="chip"
                maxSelectedLabels={2}
                selectedItemsLabel="{0} branches"
                placeholder={branchOptions.length === 0 ? 'No branches in this location' : 'All branches'}
                emptyMessage="No branches in this location"
                className="w-full"
              />
            </Field>
          )}

          {branchIds.length > 0 && (
            <Field label="Department">
              <MultiSelect
                value={departmentIds}
                options={departmentOptions}
                onChange={(e) => setDepartmentIds((e.value as string[]) ?? [])}
                filter
                display="chip"
                maxSelectedLabels={2}
                selectedItemsLabel="{0} departments"
                placeholder={departmentOptions.length === 0 ? 'No departments in this branch' : 'All departments'}
                emptyMessage="No departments in this branch"
                className="w-full"
              />
            </Field>
          )}
        </Section>

        {/* Campaign → Team → Agents. */}
        <Section title="Campaign">
          <Field label="Campaign">
            <MultiSelect
              value={campaignIds}
              options={options.campaigns}
              onChange={(e) => setCampaignIds((e.value as string[]) ?? [])}
              filter
              display="chip"
              maxSelectedLabels={2}
              selectedItemsLabel="{0} campaigns"
              placeholder={loading ? 'Loading…' : 'All campaigns'}
              className="w-full"
            />
          </Field>

          {campaignIds.length > 0 && (
            <Field label="Team">
              <MultiSelect
                value={teamIds}
                options={teamOptions}
                onChange={(e) => setTeamIds((e.value as string[]) ?? [])}
                filter
                display="chip"
                maxSelectedLabels={2}
                selectedItemsLabel="{0} teams"
                placeholder={teamOptions.length === 0 ? 'No teams in this campaign' : 'All teams'}
                emptyMessage="No teams in this campaign"
                className="w-full"
              />
            </Field>
          )}
        </Section>

        {/* Agents — the inbox's dual-list picker, once a team narrows it. */}
        {teamIds.length > 0 && (
          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <UserCog className="h-3.5 w-3.5 text-[#3366FF]" aria-hidden="true" />
              <span className="text-xs font-semibold text-slate-600">Agents</span>
              {agentIds.length > 0 && (
                <span className="rounded-[3px] border border-blue-200 bg-blue-50 px-1.5 py-0.5 font-mono text-[10px] font-bold text-blue-700">
                  {agentIds.length}
                </span>
              )}
            </div>
            <p className="mb-1.5 text-[11px] text-slate-400">
              Leave empty to include every agent in the selected team{teamIds.length === 1 ? '' : 's'}.
            </p>
            <DualListSelect
              options={agentOptions.map((a) => ({
                value: a.value,
                label: a.code ? `${a.label} (${a.code})` : a.label,
              }))}
              selected={agentIds}
              onChange={setAgentIds}
              availableLabel="Agents"
              selectedLabel="Selected Agents"
              searchPlaceholder="Search agents…"
              height={180}
              emptyText="No agents in this team"
            />
          </div>
        )}

        {/* The flat catalogs. */}
        <Section title="Conversation">
          {CATALOG_FIELDS.map((f) => (
            <Field key={String(f.key)} label={f.label}>
              <MultiSelect
                value={ids[f.key as string] ?? []}
                options={options[f.source] as { label: string; value: string }[]}
                onChange={(e) => setIdsFor(f.key as string, (e.value as string[]) ?? [])}
                filter
                display="chip"
                maxSelectedLabels={2}
                selectedItemsLabel={`{0} ${f.label.toLowerCase()}`}
                placeholder={loading ? 'Loading…' : `All ${f.label.toLowerCase()}`}
                className="w-full"
              />
            </Field>
          ))}
        </Section>

        <div className="mt-5 flex items-center justify-end border-t border-slate-100 pt-4">
          <button
            type="button"
            onClick={handleReset}
            className="inline-flex items-center gap-1.5 rounded-[3px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition-all hover:border-slate-300 hover:bg-slate-50"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            Reset
          </button>
        </div>
      </div>
    </ResourceDrawer>
  )
}

function Section({
  title, children,
}: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mt-5 border-t border-slate-100 pt-4">
      <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
        {title}
      </p>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  )
}

function Field({
  label, required = false, children,
}: { label: string; required?: boolean; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-slate-400">
        {label}
        {required && <span aria-hidden className="ml-1 text-red-500">*</span>}
      </label>
      {children}
    </div>
  )
}
